import PropTypes from 'prop-types';
import { useState } from 'react';
// material
import { Box, Avatar, Typography, IconButton, Tooltip } from '@mui/material';
// hooks
import useAuth from '../../hooks/useAuth';
// redux
import { useDispatch, useSelector } from '../../redux/store';
import { updateChatPin, setCall, setActiveConversation, selectActiveGroupCalls } from '../../redux/slices/internalChat';
// socket
import { socket } from '../../contexts/SocketIo';
// utils
import axios from '../../utils/axios';
// components
import Iconify from '../../components/Iconify';
import GroupInfoModal from './GroupInfoModal';
import UserStatusIndicator from './UserStatusIndicator';
// config
import { HOST_API } from '../../config';

// ----------------------------------------------------------------------

InternalChatHeader.propTypes = {
  chat: PropTypes.object.isRequired,
  presence: PropTypes.object,
  pinnedMessageContent: PropTypes.object,
  onScrollToMessage: PropTypes.func,
};

export default function InternalChatHeader({ chat, presence, pinnedMessageContent, onScrollToMessage }) {
  const dispatch = useDispatch();
  const { user } = useAuth();
  const [openInfo, setOpenInfo] = useState(false);

  const activeGroupCalls = useSelector(selectActiveGroupCalls);
  const activeCallParams = activeGroupCalls[chat._id];

  // Verifica se o usuário logado é admin do grupo
  const meParticipant = chat.participants?.find((p) => String(p.userId._id || p.userId) === String(user._id));
  const isAdmin = meParticipant?.isAdmin;

  // Em grupos: só admin pode fixar/desfixar. Em conversas privadas: qualquer participante pode.
  const canPin = chat.type === 'group' ? isAdmin : !!meParticipant;

  const handleBack = () => {
    dispatch(setActiveConversation(null));
  };

  const handleUnpin = async (e) => {
    e.stopPropagation(); // não aciona o scroll
    try {
      await axios.put(`/api/internal-chat/${chat._id}/pin`, {}); // sem messageId = unpin
      dispatch(updateChatPin({ chatId: chat._id, pinnedMessage: null }));
    } catch (error) {
      console.error('Failed to unpin message:', error);
    }
  };

  const getRecipientStatus = () => {
    if (chat.type === 'group') return null;
    const other = chat.participants.find((p) => String(p.userId._id || p.userId) !== String(user._id));
    const rId = String(other?.userId?._id || other?.userId);
    return presence[rId]?.online ? 'online' : 'offline';
  };

  const getStatusText = () => {
    if (!chat || chat.type === 'group') {
      return `${chat.participants.length} participantes`;
    }

    const otherParticipant = chat.participants.find((p) => String(p.userId._id || p.userId) !== String(user._id));
    const status = otherParticipant ? presence[otherParticipant.userId._id || otherParticipant.userId] : null;

    if (!status) return 'Offline';
    if (status.online) return 'Online';

    if (!status.lastSeenAt) return 'Offline';

    const lastSeenDate = new Date(status.lastSeenAt);
    const isToday = new Date().toDateString() === lastSeenDate.toDateString();

    const timeStr = lastSeenDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    if (isToday) {
      return `Visto por último hoje às ${timeStr}`;
    }
    return `Visto por último em ${lastSeenDate.toLocaleDateString()} às ${timeStr}`;
  };

  // Determina o preview de texto da mensagem fixada
  const getPinnedPreview = () => {
    if (pinnedMessageContent?.content) {
      return pinnedMessageContent.content;
    }
    if (pinnedMessageContent?.attachments?.length > 0) {
      return '📎 Anexo';
    }
    return 'Ver mensagem fixada';
  };

  const handlePinnedClick = () => {
    if (chat.pinnedMessage?.messageId && onScrollToMessage) {
      onScrollToMessage(String(chat.pinnedMessage.messageId));
    }
  };

  const handleStartCall = (type) => {
    let partner = null;
    if (chat.type === 'group') {
      partner = { _id: chat._id, name: chat.name, avatar: chat.avatar };
    } else {
      const other = chat.participants.find((p) => String(p.userId._id || p.userId) !== String(user._id));
      partner = other?.userId || { _id: other?.userId?._id || other?.userId, name: chat.name, avatar: chat.avatar };
    }

    const participantIds = chat.participants.map((p) => p.userId._id || p.userId);

    // Update Redux State
    dispatch(
      setCall({
        status: 'calling',
        isIncoming: false,
        chatId: chat._id,
        type,
        partner,
        participantIds,
      })
    );

    // Signaling
    socket.emit('internal_chat:call_initiate', {
      chatId: chat._id,
      targetId: chat.type === 'private' ? partner._id : chat._id,
      type,
      participantIds,
    });
  };

  const handleJoinCall = () => {
    const participantIds = chat.participants.map((p) => p.userId._id || p.userId);

    let partner = null;
    if (chat.type === 'group') {
      partner = { _id: chat._id, name: chat.name, avatar: chat.avatar };
    }

    // Entrar com o mesmo tipo da call pai
    const type = activeCallParams?.type || 'audio';

    // Set status directly to active since we are joining an existing one
    dispatch(
      setCall({
        status: 'active',
        isIncoming: false,
        chatId: chat._id,
        type,
        partner,
        participantIds,
      })
    );

    socket.emit('internal_chat:call_join', {
      chatId: chat._id,
      participantIds,
    });
  };

  return (
    <Box sx={{ flexDirection: 'column', width: '100%' }}>
      {/* Main Header */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid rgba(145, 158, 171, 0.24)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={handleBack} sx={{ mr: 1, display: { md: 'none' } }}>
            <Iconify icon="eva:arrow-ios-back-fill" width={24} height={24} />
          </IconButton>

          {chat.type === 'private' ? (
            <UserStatusIndicator status={getRecipientStatus()}>
              <Avatar src={`${HOST_API}${chat.avatar}`} alt={chat.name} sx={{ mr: 2 }}>
                {chat.name?.charAt(0)}
              </Avatar>
            </UserStatusIndicator>
          ) : (
            <Avatar src={`${HOST_API}${chat.avatar}`} alt={chat.name} sx={{ mr: 2 }}>
              {chat.name?.charAt(0)}
            </Avatar>
          )}
          <Box sx={{ ml: chat.type === 'private' ? 1.5 : 0 }}>
            <Typography variant="subtitle2">{chat.name}</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {getStatusText()}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {activeCallParams ? (
            <Box
              onClick={handleJoinCall}
              sx={{
                px: 2,
                py: 0.75,
                borderRadius: 1.5,
                bgcolor: 'success.main',
                color: 'common.white',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                cursor: 'pointer',
                typography: 'button',
                '&:hover': {
                  bgcolor: 'success.dark',
                },
              }}
            >
              <Iconify icon={activeCallParams?.type === 'video' ? 'eva:video-fill' : 'eva:phone-fill'} width={20} />
              Entrar na Chamada
            </Box>
          ) : (
            <>
              <Tooltip title="Chamada de voz">
                <IconButton onClick={() => handleStartCall('audio')} color="primary">
                  <Iconify icon="eva:phone-fill" width={22} height={22} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Chamada de vídeo">
                <IconButton onClick={() => handleStartCall('video')} color="primary">
                  <Iconify icon="eva:video-fill" width={22} height={22} />
                </IconButton>
              </Tooltip>
            </>
          )}

          <IconButton onClick={() => setOpenInfo(true)}>
            <Iconify icon="eva:info-fill" width={24} height={24} />
          </IconButton>
        </Box>
      </Box>

      {/* Pinned Message Banner — estilo WhatsApp */}
      {chat.pinnedMessage?.messageId && (
        <Box
          onClick={handlePinnedClick}
          sx={{
            px: 2,
            py: 0.75,
            bgcolor: 'background.neutral',
            borderBottom: '1px solid rgba(145, 158, 171, 0.24)',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            cursor: 'pointer',
            transition: 'background-color 0.15s',
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          {/* Barra colorida lateral (estilo WhatsApp) */}
          <Box
            sx={{
              width: 3,
              alignSelf: 'stretch',
              borderRadius: 1,
              bgcolor: 'primary.main',
              flexShrink: 0,
            }}
          />

          <Iconify icon="eva:pin-fill" sx={{ color: 'primary.main', width: 16, height: 16, flexShrink: 0 }} />

          <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
            <Typography
              variant="caption"
              sx={{ color: 'primary.main', display: 'block', fontWeight: 'bold', lineHeight: 1.2 }}
            >
              Mensagem Fixada
            </Typography>
            <Typography variant="body2" noWrap sx={{ color: 'text.secondary', lineHeight: 1.4 }}>
              {getPinnedPreview()}
            </Typography>
          </Box>

          {/* Botão desfixar — grupos: só admin; privadas: qualquer um */}
          {canPin && (
            <Tooltip title="Desfixar mensagem">
              <IconButton
                size="small"
                onClick={handleUnpin}
                sx={{ flexShrink: 0, color: 'text.disabled', '&:hover': { color: 'error.main' } }}
              >
                <Iconify icon="eva:close-fill" width={16} height={16} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}

      {/* Info Modal */}
      <GroupInfoModal open={openInfo} onClose={() => setOpenInfo(false)} chat={chat} />
    </Box>
  );
}
